import { useEffect, useRef, useState } from "react";
import { api, apiUpload, currentUser, downloadDocument } from "../lib/api";
import { formatQAR } from "../lib/money";
export const EXPENSE_TYPES = ["Court Fee", "Lawyer Fee", "Enforcement Fee", "Notary / Attestation", "Travel", "Courier", "Other"];
const today = () => new Date().toISOString().slice(0, 10);
/** Add-expense form for one case; optional receipt is uploaded against the new expense. */
export function ExpenseForm({ caseId, onSaved }) {
    const [type, setType] = useState(EXPENSE_TYPES[0]);
    const [date, setDate] = useState(today());
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const fileInput = useRef(null);
    async function save() {
        const value = Number(amount);
        if (!amount || Number.isNaN(value) || value <= 0) {
            setError("Enter an amount greater than zero.");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const r = await api.post(`/cases/${caseId}/expenses`, {
                expenseDate: new Date(date).toISOString(),
                expenseType: type,
                amount: value,
                description: description || undefined,
            });
            const file = fileInput.current?.files?.[0];
            if (file) {
                const form = new FormData();
                form.append("file", file);
                form.append("documentType", "PAYMENT_RECEIPT");
                form.append("expenseId", r.expense.id);
                try {
                    await apiUpload(`/cases/${caseId}/documents`, form);
                }
                catch {
                    setError("Expense saved, but the receipt upload failed (PDF, PNG, JPEG, WebP up to 15MB).");
                }
            }
            setAmount("");
            setDescription("");
            if (fileInput.current)
                fileInput.current.value = "";
            onSaved(r.case);
        }
        catch {
            setError("Couldn't add the expense.");
        }
        finally {
            setBusy(false);
        }
    }
    return (<div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select value={type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
          {EXPENSE_TYPES.map((t) => (<option key={t}>{t}</option>))}
        </select>
        <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} style={inputStyle}/>
        <input type="number" min="0" step="0.01" placeholder="Amount (QAR)" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ ...inputStyle, width: 130 }}/>
        <input placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 160 }}/>
        <input ref={fileInput} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" style={{ fontSize: "0.78rem", maxWidth: 200 }} title="Receipt (optional)"/>
        <button onClick={save} disabled={busy} style={btnStyle}>
          {busy ? "Saving…" : "Add expense"}
        </button>
      </div>
      {error && <div style={{ color: "var(--st-risk)", fontSize: "0.82rem", marginTop: 8 }}>{error}</div>}
    </div>);
}
export function ExpensePanel({ theCase, onCaseUpdated }) {
    const [items, setItems] = useState([]);
    const isManagement = currentUser()?.role === "MANAGEMENT";
    function load() {
        api
            .get(`/cases/${theCase.id}/expenses`)
            .then((r) => setItems(r.items))
            .catch(() => { });
    }
    useEffect(load, [theCase.id]);
    async function remove(id) {
        if (!window.confirm("Remove this expense? The amount will be taken off what the customer owes."))
            return;
        const r = await api.delete(`/cases/${theCase.id}/expenses/${id}`);
        onCaseUpdated(r.case);
        load();
    }
    const total = items.reduce((s, e) => s + e.amount, 0);
    return (<div style={{ marginTop: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Additional Expenses</h3>
        <span className="mono" style={{ fontSize: "0.8rem", color: "var(--ink-soft)" }}>
          Total {formatQAR(total)} (added to amount recoverable)
        </span>
      </div>
      <ExpenseForm caseId={theCase.id} onSaved={(c) => {
            onCaseUpdated(c);
            load();
        }}/>
      <div style={{ marginTop: 14 }}>
        {items.length === 0 && <div style={{ color: "var(--ink-faint)", fontSize: "0.85rem" }}>No additional expenses recorded.</div>}
        {items.map((e) => (<div key={e.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 0", borderTop: "1px solid var(--line-soft)", fontSize: "0.85rem" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>
                {e.expenseType} · {formatQAR(e.amount)}
              </div>
              <div className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint)" }}>
                {new Date(e.expenseDate).toLocaleDateString()}
                {e.description ? ` · ${e.description}` : ""}
              </div>
              {e.attachments?.map((a) => (<button key={a.id} onClick={() => downloadDocument(theCase.id, a.id, a.fileName)} style={linkBtn}>
                  📎 {a.fileName}
                </button>))}
            </div>
            {isManagement && (<button onClick={() => remove(e.id)} style={{ ...linkBtn, color: "var(--st-risk)" }}>
                Remove
              </button>)}
          </div>))}
      </div>
    </div>);
}
const inputStyle = { border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", fontSize: "0.82rem", fontFamily: "inherit" };
const btnStyle = { background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: "0.82rem", padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer" };
export const linkBtn = { background: "transparent", border: "none", color: "var(--accent-ink)", fontWeight: 600, fontSize: "0.76rem", padding: "3px 8px 3px 0", cursor: "pointer" };
